import React from 'react';
import { Container, Typography, Grid, Button, Box, Card, CardContent } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import { motion } from 'framer-motion';
import NetworkBackground from '../components/Background/NetworkBackground';
import Plans from './Plans';
import Coverage from './Coverage';
import Contact from './Contact';
import FAQ from './FAQ';

const Home = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', color: 'white', position: 'relative' }}>
      <NetworkBackground />

      {/* Hero Section */}
      <Box
        id="home"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '3rem', md: '4.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              DEVOLVED TELCO LIMITED
            </Typography>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                mb: 4,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              }}
            >
              Empowering Embu with Fast & Reliable Internet Solutions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => scrollToSection('plans')}
                sx={{ 
                  py: 2, 
                  px: 4,
                  fontSize: '1.2rem',
                  backdropFilter: 'blur(10px)',
                  background: 'linear-gradient(45deg, #2196f3 30%, #00e5ff 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1976d2 30%, #00b8d4 90%)',
                  },
                }}
              >
                View Plans
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                href="tel:+254716504441"
                startIcon={<PhoneIcon />}
                sx={{ 
                  py: 2, 
                  px: 4,
                  fontSize: '1.2rem',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: '#00e5ff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Call: 0716 504 441
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Coverage Check */}
      <Box
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          py: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container>
          <Typography 
            variant="h4" 
            gutterBottom 
            align="center"
            sx={{ 
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Want to check if we cover your specific location?
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 3
            }}
          >
            Contact us at 0716 504 441 to verify coverage in your area
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => scrollToSection('contact')}
              sx={{ 
                py: 2, 
                px: 4,
                fontSize: '1.1rem',
              }}
            >
              Check Coverage
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              href="tel:+254716504441"
              startIcon={<PhoneIcon />}
              sx={{ 
                py: 2, 
                px: 4,
                fontSize: '1.1rem',
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: '#00e5ff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Call 0716 504 441
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.7)', py: 8 }}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Fast Installation</Typography>
                  <Typography variant="body2">Quick and professional setup by our expert team</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>24/7 Support</Typography>
                  <Typography variant="body2">Round-the-clock customer service</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Reliable Network</Typography>
                  <Typography variant="body2">High-speed fiber infrastructure</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Other Sections */}
      <Box id="plans" sx={{ pt: 8, pb: 4, bgcolor: 'rgba(0, 0, 0, 0.5)' }}>
        <Plans />
      </Box>

      {/* Coverage Section */}
      <Box id="coverage" sx={{ pt: 8, pb: 4, bgcolor: 'rgba(0, 0, 0, 0.6)' }}>
        <Coverage />
      </Box>

      {/* Coverage Check Section */}
      <Box
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          py: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container>
          <Typography 
            variant="h4" 
            gutterBottom 
            align="center"
            sx={{ 
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Want to check if we cover your specific location?
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 3
            }}
          >
            Contact us at 0716 504 441 to verify coverage in your area
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => scrollToSection('contact')}
              sx={{ 
                py: 2, 
                px: 4,
                fontSize: '1.1rem',
              }}
            >
              Check Coverage
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              href="tel:+254716504441"
              startIcon={<PhoneIcon />}
              sx={{ 
                py: 2, 
                px: 4,
                fontSize: '1.1rem',
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: '#00e5ff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Call 0716 504 441
            </Button>
          </Box>
        </Container>
      </Box>

      <Box id="contact" sx={{ pt: 8, pb: 4, bgcolor: 'rgba(0, 0, 0, 0.5)' }}>
        <Contact />
      </Box>

      <Box id="faq" sx={{ pt: 8, pb: 4, bgcolor: 'rgba(0, 0, 0, 0.6)' }}>
        <FAQ />
      </Box>
    </Box>
  );
};

export default Home;
